import React, { useState, useEffect } from "react";
import { AppDispatch } from "../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { fetchCountries, fetchRegions } from "../../../redux/actions/countries.actions";
import {assignCorporateUser, updateDealership} from "../../../redux/actions/dealership.actions";
import { ModalProps } from "../../../components/common/Interfaces/Modals";
import AssignCorporateUserModal from "./AssignCorporateUserModal";
import ModalShell, { ModalLoading } from "../../../components/common/Modals/ModalShell";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import { clearError } from "../../../redux/reducers/dealership.reducer";

const UpdateDealershipModal: React.FC<ModalProps> = ({open, setOpen, object}) => {
  const dealership = object;
  const dispatch: AppDispatch = useDispatch();
  const {error: dealershipError, loading: dealershipLoading} = useSelector((state: RootState) => state.dealership);
  const {data:countries} = useSelector((state: RootState) => state.countries);
  const {data:regions} = useSelector((state: RootState) => state.regions);

  const {canAccess:canAssignCorporate} = usePermissionCheck(['assign_corporate_user']);

  // Text Input
  const [regionInput, setRegionInput] = useState<any>(null);
  const [countryInput, setCountryInput] = useState<any>(null);
  const [corporateInput, setCorporateInput] = useState<any>(null);
  const [openEditCorporate, setOpenEditCorporate] = useState(false);

  const [submitted, setSubmitted] = useState(false);
  const [success, setSuccess] = useState(false);
  // Only reflect this modal's own requests, not the page's fetches on the same slice.
  const loading = submitted && dealershipLoading;
  const error = submitted ? dealershipError : null;

  const [updateInfo, setUpdateInfo] = useState<any>(null);

  const handleClose = () => {
    handleReset();
    setOpen(false)
  };

  useEffect(() => {
    if (open) {
      dispatch(fetchCountries());
      if (countryInput!=='' || dealership?.country?.id) {
        const countryId = countryInput || dealership?.country?.id;
        dispatch(fetchRegions(countryId));
      }
    }
  }, [dispatch,open,countryInput]);

  const handleSelect = (event: SelectChangeEvent, selectField: string) => {
    switch (selectField){
      case 'country':
        setCountryInput(event.target.value as string);
        setUpdateInfo({...updateInfo, country_id:event.target.value as string})
        setRegionInput('');
        break;

      case 'region':
        setRegionInput(event.target.value as string);
        setUpdateInfo({...updateInfo, region_id:event.target.value as string})
        break;
    }
  };

  const handleEdit = async (e:any) => {
    e.preventDefault();

    setSubmitted(true);
    let ok = true;
    if (corporateInput) {
      const result = await dispatch(assignCorporateUser({id:dealership?.id,user_id:corporateInput?.id??'none'}));
      ok = assignCorporateUser.fulfilled.match(result);
    }
    if (ok && updateInfo) {
      const result = await dispatch(updateDealership({...updateInfo,id:dealership?.id}));
      ok = updateDealership.fulfilled.match(result);
    }
    setSuccess(ok);
  }

  const handleReset = () => {
    setCountryInput(null);
    setRegionInput(null);
    setCorporateInput(null);
    setSubmitted(false);
    setSuccess(false);
    dispatch(clearError());
  }

  const getFieldName = () => {
    switch (countryInput) {
      case 1:
        return 'State'

      case 2:
          return 'Province'
      default:
          return 'Region'
    }
  }

  const done = Boolean(error) || success;

  return (
    <>
      <AssignCorporateUserModal open={openEditCorporate} setOpen={setOpenEditCorporate} selected={corporateInput??dealership?.corporate??null} setSelected={setCorporateInput}/>
      <ModalShell
        open={open}
        onClose={handleClose}
        size={loading || done ? "sm" : "md"}
        busy={loading}
        tone={loading ? undefined : error ? "error" : success ? "success" : undefined}
        title={loading ? "Updating dealership…" : error ? "An error occurred" : success ? "Dealership updated" : <>Update <strong>{dealership?.name}</strong></>}
        actions={loading ? undefined : done ?
          <button className="btn btn-primary" onClick={handleClose}>OK</button>
          :
          <>
            <button className="btn" onClick={handleClose}>Cancel</button>
            <button className="btn btn-primary" disabled={loading} onClick={handleEdit}>Update</button>
          </>
        }
      >
        {loading ?
          <ModalLoading/>
          : error ?
          <p>{error}</p>
          : success ?
          <p>Dealership successfully updated.</p>
          :
          <form className="dlg-section" style={{gap: '1rem'}} onSubmit={handleEdit}>
            <div className="dlg-row">
              <TextField
                id="name"
                label="Name"
                variant="outlined"
                fullWidth
                onChange={(e)=>setUpdateInfo({...updateInfo, name:e.target.value})}
                autoComplete="off"
                defaultValue={dealership?.name}
                />
              <TextField
                id="city"
                label="City"
                variant="outlined"
                fullWidth
                onChange={(e)=>setUpdateInfo({...updateInfo, city:e.target.value})}
                autoComplete="off"
                defaultValue={dealership?.city}
                />
            </div>

            <div className="dlg-row">
              <FormControl fullWidth>
                <InputLabel>Country</InputLabel>
                <Select
                  label="Country"
                  value={countryInput || dealership?.country?.id || ''}
                  onChange={e => handleSelect(e,'country')}
                >
                  <MenuItem value={''}>All</MenuItem>
                  { countries?.length>0 &&
                    countries.map((country:any)=>
                      <MenuItem key={country?.id} value={country?.id}>{country?.label}</MenuItem>
                    )
                  }
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{countryInput==='' || regions.length>0 ? getFieldName() : `No Regions Found.`}</InputLabel>
                <Select
                  label={countryInput==='' || regions.length>0 ? getFieldName() : `No Regions Found.`}
                  value={regionInput || dealership?.region?.id || ''}
                  onChange={e => handleSelect(e,'region')}
                  disabled={regions.length===0}
                >
                  <MenuItem value={''}>All</MenuItem>
                  { regions?.length>0 && countryInput!=='' &&
                    regions.map((region:any)=>
                      <MenuItem key={region?.id} value={region?.id}>{region?.label}</MenuItem>
                    )
                  }
                </Select>
              </FormControl>
            </div>

            <div className="dlg-current">
              <span className="dlg-label">Corporation</span>
              <span style={{display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0}}>
                <strong>{corporateInput ? corporateInput==='none'? 'None':corporateInput.name : dealership?.corporate?.name??'None'}</strong>
                {canAssignCorporate && <button type="button" className="btn btn-sm" onClick={()=>{setOpenEditCorporate(true)}}>Edit</button>}
              </span>
            </div>

            <div className="dlg-row">
              <TextField
                id="phone"
                label="Phone"
                variant="outlined"
                fullWidth
                onChange={(e)=>setUpdateInfo({...updateInfo, phone:e.target.value})}
                autoComplete="off"
                defaultValue={dealership?.phone}
                />
              <TextField
                id="email"
                label="Email"
                variant="outlined"
                fullWidth
                onChange={(e)=>setUpdateInfo({...updateInfo, email:e.target.value})}
                autoComplete="off"
                defaultValue={dealership?.email}
                />
            </div>

            <div className="dlg-row">
              <TextField
                id="website_url"
                label="Website"
                variant="outlined"
                fullWidth
                onChange={(e)=>setUpdateInfo({...updateInfo, website_url:e.target.value})}
                autoComplete="off"
                defaultValue={dealership?.website_url}
                />
              <TextField
                id="supplies_url"
                label="Supplies"
                variant="outlined"
                fullWidth
                onChange={(e)=>setUpdateInfo({...updateInfo, supplies_url:e.target.value})}
                autoComplete="off"
                defaultValue={dealership?.supplies_url}
                />
            </div>
          </form>
        }
      </ModalShell>
    </>
  );
};

export default UpdateDealershipModal;
